/**
 * Управление на каноне: очередь проверки (`/ops/verify`) и ввод за хозяйство
 * (`/ops/entry`).
 *
 * Очередь читает корпус, набранный из `/verification/queue`: районной роли
 * `/my/works` отвечает `no_scope`, и прежний корпус «моих работ» давал
 * проверяющему вечное «Очередь пуста». Работы очереди раскрываются до
 * контуров карточкой `/farm-obligations/{id}`; подтверждённое обязательство
 * уходит в «проверен» само, и после записи строка исчезает без правки на
 * клиенте. Ввод за хозяйство (`/ops/entry`) остаётся на «моих работах».
 *
 * Суждение пишется ПО ОТЧЁТУ, а не по обязательству: у записи есть автор и
 * момент, и проверка спорит именно с ней. Самопроверку отказывает сервер;
 * кнопка скрывается только затем, чтобы человек не бил в закрытую дверь.
 */

import { ApiError, api, type QueryParams } from '../../../api/client';
import {
  corpus,
  forgetCorpus,
  readCanonDataset,
  readEntryDataset,
  type RouteAdapter,
} from './corpus';
import { canonParams, numericId } from './canon';
import { notRecordIdReason, type WriteOutcome } from './cabinet';

/**
 * Исходы проверки канона. Прототип знает два жеста — «подтвердить» и
 * «опровергнуть»; канон различает у второго два разных утверждения, и
 * «опровергнуть» отображается в `needs_clarification`: хозяйству возвращают
 * право отчитаться заново, а не закрывают запись окончательно.
 */
export type CanonOutcome = 'confirmed' | 'needs_clarification' | 'rejected';

export const OUTCOME_OF_VERDICT: Record<'confirmed' | 'refuted', CanonOutcome> = {
  confirmed: 'confirmed',
  refuted: 'needs_clarification',
};

/** Одно суждение: по какому обязательству и по какой именно записи отчёта. */
export interface VerdictRow {
  obligationId: string;
  /** Запись отчёта; `null` — сервер возьмёт действующую сам. */
  executionId: number | null;
}

function refusal(cause: unknown): string {
  if (cause instanceof ApiError) return cause.message;
  return String(cause);
}

export async function verifyContours(
  rows: readonly VerdictRow[],
  body: { outcome: CanonOutcome; on: string; note: string },
  params: QueryParams,
): Promise<WriteOutcome> {
  const written: string[] = [];
  const rejected: { id: string; reason: string }[] = [];

  for (const row of rows) {
    const id = numericId(row.obligationId);
    if (id === null) {
      rejected.push({ id: row.obligationId, reason: notRecordIdReason() });
      continue;
    }
    try {
      await api.verifyObligation(
        id,
        {
          outcome: body.outcome,
          occurred_on: body.on,
          note: body.note,
          execution_id: row.executionId,
        },
        canonParams(params),
      );
      written.push(row.obligationId);
    } catch (cause) {
      rejected.push({ id: row.obligationId, reason: refusal(cause) });
    }
  }

  if (written.length > 0) forgetCorpus();
  return { written, rejected };
}

/** Право судить — из `/auth/me`: у фермера его нет, и кнопки не рисуются. */
export async function canVerify(params: QueryParams): Promise<boolean> {
  return (await corpus(params)).canVerify;
}

/** Адаптер очереди проверки: корпус из `/verification/queue`. */
export const verificationAdapter: RouteAdapter = {
  routes: ['/ops/verify'],
  read: (name, params) => readCanonDataset(name, params, 'queue'),
};

/** Адаптер ввода за хозяйство: «мои работы» ВЫБРАННОГО хозяйства (`actor_id`). */
export const entryAdapter: RouteAdapter = {
  routes: ['/ops/entry'],
  read: (name, params) => readEntryDataset(name, params),
};
